/**
 * Northern Shopper — resolve a barcode / SKU / variant id / product id
 * NorthernShopper.Barcode.scan(code)   → open the scanned-product popup
 * NorthernShopper.Barcode.lookup(code) → resolve only, no side effects
 * NorthernShopper.Barcode.add(code)    → add straight to the cart
 *
 * Types: .dev/types/ns-custom.barcode.d.ts
 * Docs: .dev/docs/09-search-and-product-filtering.md (R9.6)
 */
(function () {
	"use strict";

	var NS = (window.NorthernShopper = window.NorthernShopper || {});
	var SEARCH_FIELDS = "title,product_type,variants.title,vendor,variants.sku,variants.barcode";

	function getRootUrl() {
		var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
		return root.endsWith("/") ? root : root + "/";
	}

	function showToast(type, title, message) {
		if (NS.Toast) {
			NS.Toast.show({ type: type, title: title, message: message });
		}
	}

	function notFound(code) {
		showToast("error", "Product not found", code);
	}

	function cartLine(cart, variantId) {
		var match = null;
		((cart && cart.items) || []).forEach(function (item) {
			if (String(item.variant_id) === String(variantId)) match = item;
		});
		return match;
	}

	function postAdd(variantId, quantity) {
		return fetch(getRootUrl() + "cart/add.js", {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify({ id: variantId, quantity: quantity }),
		}).then(function (r) {
			if (!r.ok) throw new Error("add failed");
			return r.json();
		});
	}

	/** Re-reads the cart through CardQty so other tabs and the cart UI stay in sync. */
	function fetchCart() {
		if (NS.CardQty && typeof NS.CardQty.fetchCart === "function") {
			return NS.CardQty.fetchCart(true);
		}
		return fetch(getRootUrl() + "cart.js").then(function (r) {
			return r.json();
		});
	}

	function publishCart(cart) {
		if (window.FoxTheme && FoxTheme.pubsub && cart) {
			FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
		}
		if (NS.Pricing && typeof NS.Pricing.recalculateAll === "function") {
			NS.Pricing.recalculateAll();
		}
	}

	function firstAvailableVariant(product) {
		var variants = product.variants || [];
		for (var i = 0; i < variants.length; i++) {
			if (variants[i].available) return variants[i];
		}
		return variants[0] || null;
	}

	/**
	 * Exact SKU / barcode / variant-id match. Deliberately strict: a scanned code
	 * must never resolve to a different product because the search fuzzy-matched
	 * a title. Single-variant products are unambiguous, so those are accepted.
	 */
	function matchVariant(product, code) {
		var variants = product.variants || [];
		var needle = String(code).trim().toLowerCase();
		var i;
		var v;
		var sku;
		var barcode;
		for (i = 0; i < variants.length; i++) {
			v = variants[i];
			sku = v.sku != null ? String(v.sku).trim().toLowerCase() : "";
			barcode = v.barcode != null ? String(v.barcode).trim().toLowerCase() : "";
			if (sku === needle || barcode === needle || String(v.id) === String(code)) return v;
		}
		return variants.length === 1 ? variants[0] : null;
	}

	function describe(product, variant) {
		if (!product || !variant) return null;
		return {
			handle: product.handle,
			variantId: variant.id,
			title: NS.displayProductTitle(product.title),
			sku: variant.sku || "",
		};
	}

	/** First product hit for a predictive-search query, as full product JSON. */
	function suggestProduct(query) {
		var url = getRootUrl() + "search/suggest.json?q=" + encodeURIComponent(query) + "&resources[type]=product&resources[limit]=1&resources[options][unavailable_products]=show&resources[options][fields]=" + encodeURIComponent(SEARCH_FIELDS);

		return fetch(url)
			.then(function (r) {
				if (!r.ok) throw new Error("search failed");
				return r.json();
			})
			.then(function (data) {
				var products = data.resources && data.resources.results && data.resources.results.products;
				if (!products || !products.length) return null;
				return fetch(getRootUrl() + "products/" + products[0].handle + ".js").then(function (r) {
					if (!r.ok) throw new Error("product lookup failed");
					return r.json();
				});
			})
			.catch(function () {
				return null;
			});
	}

	/** SKU or barcode. */
	function lookupBySkuOrBarcode(code) {
		return suggestProduct(code).then(function (product) {
			if (!product) return null;
			return describe(product, matchVariant(product, code));
		});
	}

	/** Product id — `id:` is the only storefront search prefix that resolves ids. */
	function lookupByProductId(code) {
		return suggestProduct("id:" + code).then(function (product) {
			if (!product || String(product.id) !== String(code)) return null;
			return describe(product, firstAvailableVariant(product));
		});
	}

	/**
	 * Variant id. Variant ids are not searchable, but `/variants/{id}` 302s to
	 * `/products/{handle}?variant={id}`, so the redirect target carries both.
	 * HEAD keeps it cheap — the product page body is never downloaded.
	 */
	function lookupByVariantId(code) {
		return fetch(getRootUrl() + "variants/" + encodeURIComponent(code), { method: "HEAD" })
			.then(function (r) {
				if (!r.ok || !r.url) return null;
				var url = new URL(r.url, window.location.origin);
				var match = url.pathname.match(/\/products\/([^/?#]+)/);
				if (!match) return null;
				return {
					handle: decodeURIComponent(match[1]),
					variantId: Number(url.searchParams.get("variant") || code),
					title: "",
					sku: "",
				};
			})
			.catch(function () {
				return null;
			});
	}

	/**
	 * Resolve a scanned value to a product handle + variant id. Accepts a barcode,
	 * SKU, variant id, or product id. SKU/barcode wins because that is what a
	 * scanner emits; the numeric id paths only run when the search finds nothing.
	 * Resolves null when the value cannot be resolved.
	 */
	function lookup(code) {
		var trimmed = String(code == null ? "" : code).trim();
		if (!trimmed) return Promise.resolve(null);

		return lookupBySkuOrBarcode(trimmed).then(function (hit) {
			if (hit) return hit;
			if (!/^\d+$/.test(trimmed)) return null;
			return lookupByProductId(trimmed).then(function (byProduct) {
				if (byProduct) return byProduct;
				return lookupByVariantId(trimmed);
			});
		});
	}

	/**
	 * Show the scanned product in a popup so the shopper sets the quantity there.
	 * Nothing is added to the cart. Always resolves; never rejects.
	 */
	function scan(code) {
		var trimmed = String(code == null ? "" : code).trim();
		var miss = { ok: false, sku: trimmed, reason: "not_found" };
		var modal = NS.ScanModal;

		if (!trimmed || !modal || typeof modal.open !== "function") {
			notFound(trimmed);
			return Promise.resolve(miss);
		}

		// Popup goes up before the lookup runs, so a scan gets an immediate
		// spinner instead of waiting on the resolution round-trips.
		var token = typeof modal.showLoading === "function" ? modal.showLoading() : null;

		function fail() {
			modal.close();
			notFound(trimmed);
			return miss;
		}

		return lookup(trimmed)
			.then(function (found) {
				if (!found) return fail();
				return modal.open(found.handle, found.variantId, token).then(function (opened) {
					if (opened) {
						return {
							ok: true,
							sku: trimmed,
							title: found.title,
							variantId: found.variantId,
							handle: found.handle,
						};
					}
					// null means dismissed or superseded mid-lookup — not a miss,
					// so no error toast.
					return opened === null ? miss : fail();
				});
			})
			.catch(fail);
	}

	/**
	 * Add the resolved variant straight to the cart. Not used by the scanner —
	 * scanning opens the popup instead. Always resolves; never rejects.
	 */
	function add(code, quantity) {
		var trimmed = String(code == null ? "" : code).trim();
		var qty = Math.max(1, parseInt(quantity, 10) || 1);
		var outcome = { ok: false, sku: trimmed, reason: "not_found" };

		if (!trimmed) {
			notFound(trimmed);
			return Promise.resolve(outcome);
		}

		return lookup(trimmed)
			.then(function (found) {
				if (!found) {
					notFound(trimmed);
					return outcome;
				}
				outcome.title = found.title;
				return postAdd(found.variantId, qty)
					.then(fetchCart)
					.then(function (cart) {
						var line = cartLine(cart, found.variantId);
						var lineQty = line ? line.quantity : 0;
						var title = NS.displayProductTitle(found.title || (line && (line.product_title || line.title)) || "");
						showToast("success", title, "Added to cart · qty " + lineQty);
						publishCart(cart);
						return {
							ok: true,
							sku: trimmed,
							title: title,
							variantId: found.variantId,
							quantity: lineQty,
							cart: cart,
						};
					})
					.catch(function () {
						outcome.reason = "add_failed";
						showToast("error", found.title || "Could not add to cart", found.title ? "Could not add to cart" : trimmed);
						return outcome;
					});
			})
			.catch(function () {
				notFound(trimmed);
				return outcome;
			});
	}

	NS.Barcode = {
		scan: scan,
		lookup: lookup,
		add: add,
	};
})();
